import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = ({ children }: { children: ReactNode }) => <p>{children}</p>;

const AnswerBlock = ({ children }: { children: ReactNode }) => (
  <div className="not-prose mb-5 rounded-xl border border-accent/30 bg-accent/5 p-4 sm:p-5">
    <p className="text-[1.0625rem] leading-relaxed text-foreground">
      {children}
    </p>
  </div>
);

const a_florida =
  "Do Florida residents need to travel out of state for 5-day TMS? No. Neuro Wellness in Coral Springs is a verified SAINT\u00ae provider listed on Magnus Medical's provider locator, so Floridians can get FDA-cleared SAINT\u00ae in-state, without flights or a week of hotel bills across the country.";

const faqs: LearnFAQ[] = [
  {
    question: "Do Florida residents need to travel out of state for 5-day TMS?",
    answer: a_florida,
  },
  {
    question: "Is SAINT\u00ae TMS available in Florida?",
    answer:
      "Yes. Neuro Wellness TMS Centers of America in Coral Springs, Florida offers SAINT\u00ae on the FDA-cleared Magnus Neuromodulation System. The clinic serves patients from across Florida, including Miami, Fort Lauderdale, Boca Raton, West Palm Beach, Orlando, Tampa, and Jacksonville.",
  },
  {
    question: "Where in Florida can I get SAINT\u00ae TMS?",
    answer:
      "Neuro Wellness TMS Centers of America is located at 9734 W Sample Rd, Coral Springs, FL 33065, in Broward County. Patients from throughout Florida travel to this location for the five-day SAINT\u00ae treatment course. Contact the clinic at 954-827-9793 to discuss scheduling.",
  },
  {
    question:
      "Can I drive from Miami, Fort Lauderdale or Orlando for SAINT\u00ae?",
    answer:
      "Yes. Coral Springs is approximately 30 minutes from Fort Lauderdale, 45\u201360 minutes from Miami, ~2 hours from Naples, ~2.5 hours from Fort Myers, ~3 hours from Orlando, ~3.5\u20134 hours from Tampa, and ~4.5\u20135 hours from Jacksonville. Evaluation and MRI are scheduled to keep trips to a minimum.",
  },
  {
    question: "Does Neuro Wellness offer SAINT\u00ae as an outpatient?",
    answer:
      "Yes. SAINT\u00ae is delivered in an outpatient setting while patients are awake. No anesthesia or sedation is used. Neuro Wellness provides SAINT\u00ae in a private outpatient environment with dedicated treatment suites.",
  },
];

export const saintFlorida: LearnPageData = {
  slug: "saint-tms-florida",
  title:
    "SAINT\u00ae TMS Treatment in Florida | Coral Springs | Neuro Wellness",
  description:
    "SAINT\u00ae TMS treatment in Florida at Neuro Wellness in Coral Springs. FDA-cleared 5-day fMRI-guided depression treatment. Floridians don't need to travel out of state.",
  canonical: "/saint-tms-florida",
  h1: "SAINT\u00ae TMS Treatment in Florida",
  breadcrumbLabel: "SAINT\u00ae TMS in Florida",
  crumbs: [
    { name: "Home", href: "/" },
    { name: "SAINT\u00ae TMS in Florida", href: "" },
  ],

  shortAnswer:
    "Do Florida residents need to travel out of state for 5-day TMS? No. Neuro Wellness in Coral Springs is a verified SAINT\u00ae provider listed on Magnus Medical's provider locator, so Floridians can get FDA-cleared SAINT\u00ae in-state, without flights or a week of hotel bills across the country.",
  keyTakeaways: [
    "Florida residents do NOT need to travel out of state for FDA-cleared 5-day SAINT\u00ae TMS.",
    "Neuro Wellness in Coral Springs is a Verified SAINT\u00ae Provider listed on Magnus Medical's provider locator.",
    "Drive times: Naples ~2 hrs, Fort Myers ~2.5 hrs, Orlando ~3 hrs, Tampa ~3.5\u20134 hrs, Jacksonville ~4.5\u20135 hrs.",
    "Evaluation and MRI scans are scheduled efficiently to keep total trips to a minimum.",
    "Free insurance benefits check available; self-pay from $25,000.",
  ],

  sections: [
    {
      heading:
        "Do Florida residents need to travel out of state for 5-day TMS?",
      body: (
        <>
          <AnswerBlock>{a_florida}</AnswerBlock>
          <P>
            Some out-of-state clinics promote 5-day accelerated TMS programs to
            Florida residents, encouraging them to buy airline tickets and stay
            in hotels across the country. But Floridians do not need to leave
            the state to get FDA-cleared SAINT\u00ae.
          </P>
          <P>
            Neuro Wellness TMS Centers of America in Coral Springs, Florida is a
            Verified SAINT\u00ae Provider listed on Magnus Medical's official
            provider locator. You can receive the full 5-day, 50-session
            fMRI-guided SAINT\u00ae course right here in South Florida.
          </P>
        </>
      ),
    },
    {
      heading: "Driving in from elsewhere in Florida",
      body: (
        <>
          <P>
            If you live in Florida outside Broward County, driving to Coral
            Springs for treatment week avoids air travel entirely. Approximate
            drive times to our clinic at 9734 W Sample Rd, Coral Springs, FL
            33065:
          </P>
          <ul className="my-4 space-y-2 text-foreground">
            <li className="relative pl-5 before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              <strong>Naples:</strong> ~2 hours via I-75 East (Alligator Alley)
            </li>
            <li className="relative pl-5 before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              <strong>Fort Myers / Cape Coral:</strong> ~2.5 hours
            </li>
            <li className="relative pl-5 before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              <strong>Orlando / Central Florida:</strong> ~3 hours via Florida's
              Turnpike
            </li>
            <li className="relative pl-5 before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              <strong>Tampa Bay Area:</strong> ~3.5 to 4 hours
            </li>
            <li className="relative pl-5 before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              <strong>Jacksonville:</strong> ~4.5 to 5 hours via I-95 South
            </li>
          </ul>
          <P>
            To make treatment convenient for patients driving in, your
            preliminary evaluation consultation can be conducted by phone or
            video. The pre-treatment brain MRI scan and treatment schedule are
            coordinated so that typical patients make only one travel week trip
            to Coral Springs.
          </P>
        </>
      ),
    },
    {
      heading: "What Is SAINT\u00ae TMS?",
      body: (
        <>
          <P>
            SAINT\u00ae is an accelerated form of transcranial magnetic
            stimulation (TMS) developed at Stanford University. It delivers 50
            stimulation sessions over five consecutive days \u2014 ten short
            sessions per day, each about ten minutes, with roughly 50-minute
            breaks between sessions. The stimulation target is personalized
            using each patient's own functional MRI (fMRI) brain scan.
          </P>
          <P>
            SAINT\u00ae is FDA-cleared (510(k) K220177, September 2022) for
            adults with major depressive disorder who have not achieved
            satisfactory improvement from prior antidepressant medication in the
            current episode.
          </P>
        </>
      ),
    },
    {
      heading: "Who May Consider SAINT\u00ae in Florida",
      body: (
        <>
          <P>
            SAINT\u00ae may be appropriate for adults 22 and older with major
            depressive disorder whose symptoms have not adequately improved with
            antidepressant medication. Eligibility is determined by a qualified
            psychiatrist through an evaluation that reviews medication history,
            screens for contraindications (such as magnetic-sensitive metal in
            the head), and confirms that an fMRI can be safely performed.
          </P>
        </>
      ),
    },
    {
      heading: "Insurance and self-pay in Florida",
      body: (
        <>
          <P>
            We perform a free insurance benefits check for all Florida patients.
            Commercial plans decide coverage case by case via prior
            authorization. For self-pay patients, SAINT\u00ae at Neuro Wellness
            starts at $25,000 for the full 5-day course, including the brain
            MRI, physician oversight, all 50 sessions, and follow-up.
          </P>
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification \u2014 Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  safetyBlock:
    "SAINT\u00ae is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT\u00ae is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. Individual results vary. No outcome is guaranteed. Coverage varies by plan.",
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/saint-tms-florida#webpage`,
        name: "SAINT\u00ae TMS Treatment in Florida",
        url: `${SITE_URL}/saint-tms-florida`,
        description:
          "SAINT\u00ae TMS treatment in Florida at Neuro Wellness in Coral Springs. FDA-cleared 5-day fMRI-guided depression treatment. Floridians don't need to travel out of state.",
      }),
      orgNode,
      faqJsonld(faqs, `${SITE_URL}/saint-tms-florida#faq`),
      breadcrumbJsonld([
        { name: "Home", href: "/" },
        { name: "SAINT\u00ae TMS in Florida", href: "" },
      ]),
    ],
  },
  relatedLinks: [
    {
      title: "SAINT\u00ae Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol at our clinic",
    },
    {
      title: "SAINT\u00ae TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "Pricing and insurance verification",
    },
    {
      title: "Does SAINT\u00ae Require an MRI?",
      href: "/learn/does-saint-tms-require-an-mri",
      description: "Why the brain scan is required",
    },
  ],
};
